'use client'

import React from 'react'
import type { Locale } from '@/i18n'
import type { Footer } from '@/payload-types'
import { Facebook, Instagram, Twitter, Send, Youtube, Linkedin, Github } from 'lucide-react'
import Link from 'next/link'
import { trackSocialLinkClick } from '@/utilities/analytics'

// TikTok icon - not available in lucide-react
const TikTokIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
)

// Platform icon mapping
const platformIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  facebook: Facebook,
  instagram: Instagram,
  twitter: Twitter,
  telegram: Send,
  youtube: Youtube,
  tiktok: TikTokIcon,
  linkedin: Linkedin,
  github: Github,
}

// Platform display names
const platformNames: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  twitter: 'Twitter / X',
  telegram: 'Telegram',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  linkedin: 'LinkedIn',
  github: 'GitHub',
}

interface SocialLinksProps {
  locale?: Locale
  socialLinks?: Footer['socialLinks']
}

export function SocialLinks({ locale, socialLinks }: SocialLinksProps) {
  // If no links configured, don't render anything
  if (!socialLinks || socialLinks.length === 0) {
    return null
  }

  const handleClick = (platform: string, url: string) => {
    trackSocialLinkClick(platform, url)
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="font-semibold text-sm">
        {locale === 'uk' ? 'Соціальні мережі' : 'Social Media'}
      </h3>
      <div className="flex flex-col gap-2">
        {socialLinks.map((social, index) => {
          const Icon = platformIcons[social.platform] || Facebook
          const name = platformNames[social.platform] || social.platform
          return (
            <Link
              key={`${social.platform}-${index}`}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group"
              onClick={() => handleClick(social.platform, social.url)}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="group-hover:underline">{name}</span>
            </Link>
          )
        })}
      </div>
    </div>
  )
}
